import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { categories, getMoviesByCategory } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

const categoryMeta: Record<string, { title: string; description: string; keywords: string }> = {
  movie: {
    title: '电影 - 被窝影院高清电影在线观看',
    description: '被窝影院电影频道提供最新高清电影免费在线观看，涵盖动作片、喜剧片、科幻片、爱情片、恐怖片等各类电影大全，每日更新，不缓冲不卡顿。',
    keywords: '电影,高清电影,电影在线观看,免费电影,最新电影,动作片,喜剧片,科幻片,被窝影院',
  },
  tv: {
    title: '电视剧 - 被窝影院高清电视剧全集在线观看',
    description: '被窝影院电视剧频道提供最新电视剧全集免费在线观看，涵盖国产剧、韩剧、美剧、日剧等热播电视剧大全，高清画质不缓冲。',
    keywords: '电视剧,高清电视剧,电视剧全集,电视剧在线观看,免费电视剧,国产剧,韩剧,美剧,被窝影院',
  },
  anime: {
    title: '动漫 - 被窝影院高清动漫在线观看',
    description: '被窝影院动漫频道提供最新国漫、日漫高清在线观看，涵盖热血、奇幻、恋爱、搞笑等各类动漫大全，免费全集在线看不缓冲。',
    keywords: '动漫,高清动漫,动漫在线观看,免费动漫,国漫,日漫,热血动漫,被窝影院',
  },
  variety: {
    title: '综艺 - 被窝影院热门综艺在线观看',
    description: '被窝影院综艺频道提供最新热门综艺节目免费在线观看，涵盖真人秀、音乐竞技、旅行综艺等各类综艺大全，搞笑轻松不缓冲。',
    keywords: '综艺,综艺节目,综艺在线观看,免费综艺,真人秀,音乐综艺,旅行综艺,被窝影院',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'movie');
  const [movies, setMovies] = useState<Movie[]>([]);
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'popular'>('latest');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeSlug]);

  useEffect(() => {
    if (slug && slug !== activeSlug) {
      setActiveSlug(slug);
    }
  }, [slug, activeSlug]);

  useEffect(() => {
    let data = getMoviesByCategory(activeSlug);
    if (sortBy === 'rating') {
      data = [...data].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'popular') {
      data = [...data].sort((a, b) => b.views - a.views);
    }
    setMovies(data);
  }, [activeSlug, sortBy]);

  const currentCat = categories.find((c) => c.slug === activeSlug);
  const meta = categoryMeta[activeSlug] || categoryMeta.movie;

  return (
    <>
      <SEO
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={`https://www.beiwoyingyuan.com/category/${activeSlug}`}
      />

      <Navbar />

      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: currentCat?.name || '分类' },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-6">
          <div className="max-w-[1400px] mx-auto">
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 mb-1">
              {currentCat?.name}大全
            </h1>
            <p className="text-sm text-foreground-500 mb-6">{meta.description}</p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-1 bg-background-900 rounded-full p-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveSlug(cat.slug)}
                    className={`px-4 py-2 text-sm rounded-full transition-all whitespace-nowrap ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-950 font-label font-semibold'
                        : 'text-foreground-400 hover:text-foreground-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="flex items-center gap-1 bg-background-900/50 rounded-full p-1">
                {[
                  { value: 'latest', label: '最新' },
                  { value: 'rating', label: '评分' },
                  { value: 'popular', label: '热度' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value as typeof sortBy)}
                    className={`px-3 py-1.5 text-xs rounded-full transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === opt.value
                        ? 'bg-background-700 text-foreground-100'
                        : 'text-foreground-500 hover:text-foreground-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {movies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
                {movies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} variant="poster" />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-foreground-500">
                <span className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-800 mb-3">
                  <i className="ri-movie-2-line text-2xl"></i>
                </span>
                <p className="text-sm">暂无相关内容</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}